import React, { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import ClinicalPhasePill from "../../../shared/ClinicalPhasePill";
import StatusPill from "../../../shared/StatusPill";
import type { TargetFormValues } from "../TargetBuilderPanel.types";
import {
  IMPLEMENTATION_SETTING_OPTIONS,
  PRIMARY_IMPLEMENTER_OPTIONS,
} from "../TargetBuilderPanel.types";
import PreviewCard from "./PreviewCard";
import {
  ResourcesRow,
  SplitRow,
  TargetCriteriaRows,
  TargetMeasurementRows,
  ValueText,
  labelFor,
} from "./TargetValueRows";

const SECTION_IDS = [
  "target-section-overview",
  "target-section-measurement",
  "target-section-criteria",
] as const;

type SectionId = (typeof SECTION_IDS)[number];

interface TargetPreviewPaneProps {
  /** Goal's behavior subject — passed through to `TargetMeasurementRows` so
   * caregiver-only field visibility (e.g. the NET count-type row) stays in
   * sync with the form. */
  behaviorSubject?: "client" | "caregiver";
}

/**
 * Sticky preview pane (right column).
 *
 * The cards mirror Formik state in real time via the shared row blocks in
 * `TargetValueRows` (also used by the target-detail sidebar). Scrollspy
 * highlights the card matching the currently-visible form section.
 */
const TargetPreviewPane: React.FC<TargetPreviewPaneProps> = ({ behaviorSubject }) => {
  const { values } = useFormikContext<TargetFormValues>();
  const [activeSection, setActiveSection] = useState<SectionId>("target-section-overview");

  // Scrollspy — observe each section and pick the one most in-view.
  useEffect(() => {
    const elements = SECTION_IDS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null
    );
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveSection(visible[0].target.id as SectionId);
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const typeLabel =
    values.target_type === "skill-acquisition" ? "Skill Acquisition" : "Behavior Reduction";

  return (
    <Stack
      component="aside"
      aria-label="Target preview"
      gap={5}
      sx={{
        display: { xs: "none", lg: "flex" },
        position: "sticky",
        top: 24,
        alignSelf: "flex-start",
        width: 340,
        flexShrink: 0,
      }}
    >
      <Typography component="h3" variant="caption" color="text.secondary">
        Live Preview
      </Typography>

      <PreviewCard title="Target Overview" active={activeSection === "target-section-overview"}>
        <SplitRow label="Phase">
          <ClinicalPhasePill phase={values.phase} />
        </SplitRow>
        <SplitRow label="Status">
          <StatusPill status={values.status} />
        </SplitRow>
        <SplitRow label="Type">
          <ValueText>{typeLabel}</ValueText>
        </SplitRow>

        {/* The form only asks the replacement/FCR question for skill-acquisition
            targets — mirror that visibility here. Only the answer is surfaced;
            the FCR detail fields stay form-only. */}
        {values.target_type === "skill-acquisition" && (
          <SplitRow label="Replacement behavior">
            <ValueText>{values.is_replacement_behavior ? "Yes" : "No"}</ValueText>
          </SplitRow>
        )}

        <SplitRow label="Implementer">
          <ValueText>{labelFor(values.primary_implementer, PRIMARY_IMPLEMENTER_OPTIONS)}</ValueText>
        </SplitRow>
        <SplitRow label="Setting">
          <ValueText>
            {labelFor(values.implementation_setting, IMPLEMENTATION_SETTING_OPTIONS)}
          </ValueText>
        </SplitRow>

        <ResourcesRow resources={values.resources} />
      </PreviewCard>

      <PreviewCard
        title="Measurement & instruction design"
        active={activeSection === "target-section-measurement"}
      >
        <TargetMeasurementRows values={values} behaviorSubject={behaviorSubject} />
      </PreviewCard>

      <PreviewCard title="Mastery criteria" active={activeSection === "target-section-criteria"}>
        <TargetCriteriaRows values={values} />
      </PreviewCard>
    </Stack>
  );
};

export default TargetPreviewPane;
